import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Loader2, ShoppingBag, Trash2, ShoppingCart, Sparkles, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useWishlist } from '../contexts/WishlistContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL.replace(/\/$/, '')}/api` : '/api';

export const Wishlist = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToCart } = useCart();
  const { wishlistIds, toggleWishlist } = useWishlist();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removingId, setRemovingId] = useState(null);
  const [addingToCartId, setAddingToCartId] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
  }, [user]);

  useEffect(() => {
    // Fetch product details whenever wishlistIds changes
    fetchWishlistProducts();
  }, [wishlistIds]);

  const fetchWishlistProducts = async () => {
    try {
      setLoading(true);
      if (wishlistIds.length === 0) {
        setProducts([]);
        return;
      }

      const productPromises = wishlistIds.map((id) =>
        axios.get(`${API}/products/${id}`, { withCredentials: true })
      );
      const results = await Promise.allSettled(productPromises);

      const okProducts = results
        .filter((r) => r.status === 'fulfilled')
        .map((r) => r.value.data);

      // Evita "piscar" e sumir tudo caso 1 request falhe
      if (okProducts.length > 0) {
        setProducts(okProducts);
      } else {
        toast.error('Não foi possível carregar os itens dos favoritos');
      }
    } catch (error) {
      console.error('Error fetching wishlist products:', error);
      // Não zera a UI em erro transitório (evita flicker)
      toast.error('Erro ao carregar favoritos');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFromWishlist = async (productId) => {
    setRemovingId(productId);
    try {
      await toggleWishlist(productId);
      setProducts(products.filter(p => p.id !== productId));
      toast.success('Removido dos favoritos');
    } catch (error) {
      toast.error('Erro ao remover dos favoritos');
    } finally {
      setRemovingId(null);
    }
  };

  const handleAddToCart = async (product) => {
    setAddingToCartId(product.id);
    try {
      await addToCart(product.id, 1);
      toast.success('Adicionado ao carrinho!');
    } catch (error) {
      toast.error('Erro ao adicionar ao carrinho');
    } finally {
      setAddingToCartId(null);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#0f172a] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-pink-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 dark:text-slate-400">Carregando favoritos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0f172a] pt-24 pb-12">
      {/* Hero Header */}
      <div className="bg-gradient-to-r from-pink-500 via-rose-500 to-red-500 py-12 mb-8">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
              <Heart className="h-7 w-7 text-white fill-white" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-white">Meus Favoritos</h1>
              <p className="text-pink-100">{products.length} {products.length === 1 ? 'produto salvo' : 'produtos salvos'}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4">
        {products.length > 0 ? (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-lg border border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-pink-100 dark:bg-pink-900/30 rounded-xl flex items-center justify-center">
                    <Heart className="h-6 w-6 text-pink-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900 dark:text-white">{products.length}</p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Itens Salvos</p>
                  </div>
                </div>
              </div>
              <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-lg border border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl flex items-center justify-center">
                    <ShoppingBag className="h-6 w-6 text-emerald-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900 dark:text-white">
                      {products.filter(p => p.stock > 0).length}
                    </p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Disponíveis</p>
                  </div>
                </div>
              </div>
              <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-lg border border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
                    <Sparkles className="h-6 w-6 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900 dark:text-white">
                      {formatCurrency(products.reduce((sum, p) => sum + p.price, 0))}
                    </p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Valor Total</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Products Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="wishlist-products">
              {products.map((product, index) => (
                <div 
                  key={product.id}
                  className="group bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700 overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                >
                  {/* Product Image */}
                  <div className="relative aspect-[4/3] overflow-hidden cursor-pointer" onClick={() => navigate(`/product/${product.id}`)}>
                    <img 
                      src={product.images?.[0] || 'https://via.placeholder.com/400'}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    
                    {/* Badge */}
                    <div className="absolute top-3 left-3 bg-pink-500 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                      <Heart className="h-3 w-3 fill-white" />
                      Favorito
                    </div>
                    
                    {/* Stock Badge */}
                    {product.stock <= 0 && (
                      <div className="absolute top-3 right-3 bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                        Esgotado
                      </div>
                    )}
                    
                    {/* Quick Actions on Hover */}
                    <div className="absolute bottom-4 left-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-4 group-hover:translate-y-0">
                      <Button
                        onClick={(e) => { e.stopPropagation(); handleAddToCart(product); }}
                        disabled={product.stock <= 0 || addingToCartId === product.id}
                        className="w-full bg-white/95 text-slate-900 hover:bg-white shadow-xl rounded-xl"
                      >
                        {addingToCartId === product.id ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                          <ShoppingCart className="h-4 w-4 mr-2" />
                        )}
                        Adicionar ao Carrinho
                      </Button>
                    </div>
                  </div>
                  
                  {/* Product Info */}
                  <div className="p-5">
                    <h3 
                      className="font-bold text-lg text-slate-900 dark:text-white mb-2 line-clamp-1 cursor-pointer hover:text-pink-500 transition-colors"
                      onClick={() => navigate(`/product/${product.id}`)}
                    >
                      {product.name}
                    </h3>
                    <p className="text-slate-500 dark:text-slate-400 text-sm line-clamp-2 mb-4">
                      {product.description}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-xs text-slate-400">Preço</span>
                        <p className="text-2xl font-bold text-pink-500">
                          {formatCurrency(product.price)}
                        </p>
                      </div>
                      
                      <button
                        onClick={() => handleRemoveFromWishlist(product.id)}
                        disabled={removingId === product.id}
                        className="p-3 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-all"
                        title="Remover dos favoritos"
                      >
                        {removingId === product.id ? (
                          <Loader2 className="h-5 w-5 animate-spin" />
                        ) : (
                          <Trash2 className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Continue Shopping */}
            <div className="mt-12 text-center">
              <Button 
                onClick={() => navigate('/shop')}
                size="lg"
                className="bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-400 hover:to-rose-400 rounded-full px-8"
              >
                <ShoppingBag className="h-5 w-5 mr-2" />
                Continuar Comprando
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
            </div>
          </>
        ) : (
          /* Empty State */
          <div className="max-w-lg mx-auto text-center py-16">
            <div className="w-32 h-32 bg-gradient-to-br from-pink-100 to-rose-100 dark:from-pink-900/30 dark:to-rose-900/30 rounded-full flex items-center justify-center mx-auto mb-8">
              <Heart className="h-16 w-16 text-pink-400" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
              Sua lista de favoritos está vazia
            </h2>
            <p className="text-slate-600 dark:text-slate-400 mb-8">
              Explore nossa loja e salve os produtos que você mais gosta clicando no ícone de coração!
            </p>
            <Button 
              onClick={() => navigate('/shop')}
              size="lg"
              className="bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-400 hover:to-rose-400 rounded-full px-8"
            >
              <ShoppingBag className="h-5 w-5 mr-2" />
              Explorar Loja
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};